import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ChevronIcon from '@assets/img/icons/chevron-small.svg?react';
import cn from 'classnames';

import { isKeyOf } from '@/common/lib/isKeyOf';
import {
  type PaidSpecContractType,
  type SpecContractStatusType,
  type SpecContractType,
} from '@/common/types/contract';
import { StringSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import { CompleteOverlay } from '../CompleteOverlay/CompleteOverlay';
import { ContractRibbonAnimation } from '../ContractRibbonAnimation/ContractRibbonAnimation';
import { PointQuantityWithAnimation } from '../PointQuantity/PointQuantity';

import { Background } from './Background';
import { titles } from './config';
import { ContractTimer } from './ContractTimer';
import { StatusDescription } from './StatusDescription';

import css from './styles.module.scss';

import failPng from '/img/icons/golden_contract/Fail.png';
import failWebp from '/img/icons/golden_contract/Fail.webp';

interface SpecContractItemProps {
  kind: SpecContractType | PaidSpecContractType;
  status: SpecContractStatusType;
  requairedLevel: number;
  timeLeft: number | null;
  point?: number;
  onClick?: () => void;
  isUninitialized?: boolean;
  hasContractComplete?: boolean;
  contractTimerExtensionDuration: number | null;
}

const SpecContractItem: FC<SpecContractItemProps> = ({
  kind,
  status,
  requairedLevel,
  timeLeft,
  point,
  onClick,
  isUninitialized,
  hasContractComplete,
  contractTimerExtensionDuration,
}) => {
  const titleWithOpacity = status === 'lock' || status === 'wait';
  const { t } = useTranslation();
  const config = {
    forViewing: 'About',
    lock: `Level ${requairedLevel}`,
    open: t('Open'),
    expired: t('Extend'),
    withTimer: ['inProgress', 'wait', 'expired', 'fail'],
    withPoints: ['open', 'inProgress', 'done', 'expired', 'fail'],
  };

  const withChevronIcon =
    status === 'forViewing' || status === 'open' || status === 'expired';
  const withTimer =
    config.withTimer.includes(status) &&
    (typeof timeLeft === 'number' ||
      typeof contractTimerExtensionDuration === 'number');
  const withPoint = config.withPoints.includes(status) && point;

  return (
    <div className={css.specContract} onClick={onClick}>
      {withPoint && <PointQuantityWithAnimation value={point} />}
      <Background kind={kind} status={status} />
      <StringSmall
        weight="bold"
        className={cn(titleWithOpacity && '!text-white/80')}
      >
        {titles[kind]}
      </StringSmall>
      {status === 'fail' && (
        <Picture
          className="aspect-[108/66] w-[6.75rem]"
          img={failPng}
          webp={failWebp}
          alt="alt"
        />
      )}
      {isKeyOf(config, status) && (
        <StatusDescription
          text={config[status]}
          variant={status === 'expired' ? 'extend' : 'default'}
          className={cn(
            withChevronIcon && 'pl-4 pr-2',
            status === 'lock' && 'px-3',
            status === 'expired' && 'mt-[6px]',
          )}
          icon={
            withChevronIcon
              ? (): ReactNode => <ChevronIcon className="w-4 h-4" />
              : undefined
          }
        />
      )}
      {withTimer && (
        <ContractTimer
          timeLeft={
            status === 'expired'
              ? contractTimerExtensionDuration || 0
              : timeLeft || 0
          }
          hasWaitStatus={status === 'wait'}
        />
      )}
      <ContractRibbonAnimation isUninitialized={!!isUninitialized} />
      {hasContractComplete && status === 'inProgress' && (
        <CompleteOverlay checkSize="small" />
      )}
    </div>
  );
};

export { SpecContractItem, type SpecContractItemProps };
